import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
// @ts-ignore
import * as Validator from 'validatorjs';

import { Select, CountryIcon, Input } from '@gamesb42/ui-kit';
import countries from 'pages/Connection/components/CountrySelect/countrieslist';
import { Title3 } from '../Typography';
// Используем Input из @gamesb42/ui-kit
import { about } from './index';
import styles from './styles.module.scss';

const Address = observer(() => {
  // Состояние для отслеживания затронутых полей
  const [touchedFields, setTouchedFields] = useState({
    country: false,
    zip: false,
    state: false,
    city: false,
    address: false,
  });

  // Подготавливаем список стран для Select
  const countriesList = useMemo(
    () =>
      countries.map((country) => {
        const Country = CountryIcon?.[country.code] || CountryIcon.UNKNOWN;
        return {
          label: (
            <div className={styles.country}>
              {Country && <Country size={16} />}
              <span className={styles.countryName}>{country.name}</span>
            </div>
          ),
          labelForSearch: country.name,
          value: country.code,
        };
      }),
    [],
  );

  // Правила валидации адреса (zip теперь не обязательный)
  const validationRules = {
    country: 'required',
    zip: 'string', // Убираем required
    state: 'string',
    city: 'required|string',
    address: 'required|string',
  };

  // Функция для пометки поля как затронутого
  const touchField = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const validation = useMemo(
    () =>
      new Validator(about.values.details, validationRules, {
        required: 'This field is required',
      }),
    [
      about.values.details.country,
      about.values.details.zip,
      about.values.details.state,
      about.values.details.city,
      about.values.details.address,
    ],
  );

  const isValid = validation.passes();

  useEffect(() => {
    about.isStepIsValid[2] = isValid;
  }, [isValid]);

  // Определяем тип компании для правильного заголовка
  const isCorporateEntity = ['partnership', 'corporation'].includes(
    about.values.details.company_form ?? '',
  );
  const isSoleProprietor =
    about.values.details.company_form === 'sole-proprietor';

  // Обработчик изменения страны
  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = countries.find((item) => item.code === countryCode);
    if (selectedCountry) {
      about.address.country = {
        name: selectedCountry.name,
        code: selectedCountry.code,
      };
    }
    about.values.details.country = countryCode;
    touchField('country');
  };

  return (
    <div>
      {/* Заголовок в зависимости от типа компании */}
      {isCorporateEntity && (
        <Title3 className={styles.subTitle}>Company full legal address*</Title3>
      )}
      {isSoleProprietor && (
        <Title3 className={styles.subTitle}>
          Individual Entrepreneur full address (registered)*
        </Title3>
      )}

      {/* Поле выбора страны */}
      <div className={styles.selectHolder} style={{ marginTop: '20px' }}>
        <Select
          label="Country*"
          size="large"
          onChange={handleCountryChange}
          onBlur={() => touchField('country')}
          value={
            about.address.country?.code &&
            countries.find((item) => item.code === about.address.country?.code)
              ?.code
          }
          showSearch
          filterOption
          optionFilterProp="labelForSearch"
          options={countriesList}
          notFoundContent={null}
          className={styles.selectCountry}
          status={
            touchedFields.country && validation.errors.first('country')
              ? 'error'
              : undefined
          }
        />
        {touchedFields.country && validation.errors.first('country') && (
          <div className={styles.errorMessage}>
            {validation.errors.first('country')}
          </div>
        )}
      </div>

      <div className={styles.post}>
        <div className={styles.postField}>
          <Input
            onChange={(e) => {
              about.values.details.zip = e.target.value;
              touchField('zip');
            }}
            onBlur={() => touchField('zip')}
            label="Postal/Zip Code"
            size="large"
            value={about.values.details.zip || ''}
            status={
              touchedFields.zip && validation.errors.first('zip')
                ? 'error'
                : undefined
            }
          />
          {touchedFields.zip && validation.errors.first('zip') && (
            <div className={styles.errorMessage}>
              {validation.errors.first('zip')}
            </div>
          )}
        </div>
        <div className={styles.postField}>
          <Input
            onChange={(e) => {
              about.values.details.state = e.target.value;
              touchField('state');
            }}
            onBlur={() => touchField('state')}
            label="State"
            size="large"
            value={about.values.details.state || ''}
            status={
              touchedFields.state && validation.errors.first('state')
                ? 'error'
                : undefined
            }
          />
          {touchedFields.state && validation.errors.first('state') && (
            <div className={styles.errorMessage}>
              {validation.errors.first('state')}
            </div>
          )}
        </div>
      </div>

      {/* Город */}
      <div className={styles.textField}>
        <Input
          onChange={(e) => {
            about.values.details.city = e.target.value;
            touchField('city');
          }}
          onBlur={() => touchField('city')}
          label="City*"
          size="large"
          value={about.values.details.city || ''}
          status={
            touchedFields.city && validation.errors.first('city')
              ? 'error'
              : undefined
          }
        />
        {touchedFields.city && validation.errors.first('city') && (
          <div className={styles.errorMessage}>
            {validation.errors.first('city')}
          </div>
        )}
      </div>

      {/* Улица */}
      <div>
        <Input
          onChange={(e) => {
            about.values.details.address = e.target.value;
            touchField('address');
          }}
          onBlur={() => touchField('address')}
          label="Street Address*"
          size="large"
          value={about.values.details.address || ''}
          status={
            touchedFields.address && validation.errors.first('address')
              ? 'error'
              : undefined
          }
        />
        {touchedFields.address && validation.errors.first('address') && (
          <div className={styles.errorMessage}>
            {validation.errors.first('address')}
          </div>
        )}
      </div>
    </div>
  );
});

export default Address;
